import _ from 'underscore';
import m from 'mithril';
import {CellItem} from "./cellItem";
import {XmlTagInfo} from "../xmlTagProvider";
import {ImageView} from "../../ui/items/imageView";
import {Questionary, QuestionaryVariable} from "../questionary";
import {Form} from "../form/form";
import Cell from "../form/cell";
import {ValidateResult} from "../contentType";
import Point from "../../ui/components/ImageHighlighter/Point";
import ImageHighlighter from "../../ui/components/imageHighlighter";

class Viewport {

    constructor (
        public x:number,
        public y:number,
        public width:number,
        public height:number
    ) {}

    static parseJson(json: string): Viewport|undefined {
        if (json == null) {
            return undefined;
        }
        var match = /^\[(\d+),(\d+),(\d+),(\d+)]$/.exec(json);
        if (match != null) {
            return new Viewport(
                parseInt(match[1]),
                parseInt(match[2]),
                parseInt(match[3]),
                parseInt(match[4])
            );
        } else {
//			console.log("Image Viewport parameter syntax error: " + json);
            return undefined;
            // throw new QuestionaryException("Image Viewport parameter syntax error");
        }
    }


}

class Size {
    width: number;
    height: number;

    static parseJson(json: string): Size|undefined {
        if (json == null) {
            return undefined;
        }
        var match = /^\[(\d+),(\d+)]$/.exec(json);
        if (match != null) {
            return new Size(parseInt(match[1]), parseInt(match[2]));
        } else {
            return undefined;
            // throw new QuestionaryException("Image Viewport parameter syntax error");
        }
    }

    constructor(width:number, height:number) {
        this.width = width;
        this.height = height;
    }
}

export class Area {
    static maxSelectionOrder = 0;
    type: string;
    points: Point[];
    styleHover: Style;
    style: Style;
    selectionOrder = 0;

    // variable is initialized in init(), trusted
    private variable!: QuestionaryVariable<any>;
    private variableName: string;

    private static parsePoints(points: string): Point[] {
        const items = JSON.parse(points);
        return Array.isArray(items) ? items.map(i => new Point(i[0], i[1])) : [];
    }

    constructor(config: XmlTagInfo) {
        this.type = config.attributes['type'];
        this.variableName = config.attributes['variable'];
        this.points = Area.parsePoints(config.attributes['points']);

        this.style = config.children
            .filter(node => node.tag === 'style' && node.attributes['type'] === 'unselected')
            .map(node => Style.fromXml(node))[0];

        this.styleHover = config.children
            .filter(node => node.tag === 'style' && node.attributes['type'] === 'hover')
            .map(node => Style.fromXml(node))[0];

    }

    isInside(point: Point) {
        // ray-casting algorithm based on
        // http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html
        // https://github.com/substack/point-in-polygon

        const vs = this.points;
        let x = point.x, y = point.y;

        let inside = false;
        for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
            const xi = vs[i].x, yi = vs[i].y;
            const xj = vs[j].x, yj = vs[j].y;

            const intersect = ((yi > y) !== (yj > y))
                && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) {
                inside = !inside
            }
        }

        return inside;
    }

    init(questionary: Questionary) {
        this.variable = questionary.variableByName(this.variableName);
    }

    getSelectedValue(): number {
        return this.variable();
    }

    setSelectedValue(value: number|null) {
        this.variable(value);
        this.selectionOrder = ++Area.maxSelectionOrder;
    }

    equals(area: Area): boolean {
        return this.variableName === area.variableName;
    }



}

export class Category {

    constructor(
        public name: string,
        public value: number,
        public min: number,
        public max: number,
        public style: Style
    ) {}

    static fromXml(config: XmlTagInfo): Category {
        return new Category(
            config.attributes['name'],
            parseInt(config.attributes['value'], 10),
            parseInt(config.attributes['min'], 10),
            parseInt(config.attributes['max'], 10),
            config.children
                .filter(node => node.tag === 'style' && node.attributes['type'] === 'selected')
                .map(node => Style.fromXml(node))[0]
        );
    }
}

export class Style {

    static fromXml(config: XmlTagInfo): Style {
        return new Style(config.attributes['fillColor'], config.attributes['borderColor']);
    }

    constructor(
        public fillColor: string,
        public borderColor: string
    ) { }
}

export class Highlighter {
    type: 'NONE'|'POINT'|'PICK_AREA'|string;
    private data: {[id: string]: Point[]};
    private areas: Area[];
    private categories: Category[];

    public comments: boolean = false;

    constructor(
        public readonly imageItem: ImageItem,
        config: XmlTagInfo
    ) {
        this.type = config.attributes['type'];
        this.areas = config.children
            .filter(node => node.tag === 'areas')
            .map(node => node.children
                .filter((node:XmlTagInfo) => node.tag === 'area')
                .map((n:XmlTagInfo) => new Area(n))
            )[0];
        this.categories = config.children
            .filter(node => node.tag === 'categories')
            .map(node => node.children
                .filter((node:XmlTagInfo) => node.tag === 'category')
                .map((n:XmlTagInfo) => Category.fromXml(n))
            )[0];
        this.data = <{[id: number]: Point[]}> this.getCategories().reduce((acc: any, def: Category) => {
            acc[def.value] = [];
            return acc
        }, {});
    }

    init(cellItem: ImageItem, questionary: Questionary) {
        // Load variable after GET /answers
        if (cellItem.getVariable()) {
            this.load(cellItem.getVariable());
        }
        this.areas.forEach(a => a.init(questionary));
    }

    getCategories() {
        return this.categories;
    }

    getCategoryByValue(value: number): Category {
        return this.categories.filter(c => c.value === value)[0];
    }

    searchPoint(category: number, coordinates: Point, remove: boolean = false): Point|null {

        const points = this.points;
        let needle = null;

        _.each(points, (cat, i: any) => {
            _.each(cat, (p: Point, j: any) => {
                const distanceBetweenPoints = this.distanceBetweenPoints(p.x, p.y, coordinates.x, coordinates.y);
                if (distanceBetweenPoints < ImageHighlighter.POINT_RADIUS) {
                    needle = p;
                    if (remove) {
                        this.data[i] = _.without(this.data[i], p);
                    }
                }
            });
        });

        return needle;
    }

    addOrRemovePoint(category: number, point: Point) {
        if (!this.searchPoint(category, point, true)) {
            if (this.canAddPoint(category)) {
                this.data[category].push(point);
                this.save();
            }
        }
    }

    canAddPoint(category: number): boolean {
        const c = this.getCategoryByValue(category);
        return (c.max === 0 || this.points[category].length < c.max);
    }


    distanceBetweenPoints(x1: number, y1: number, x2: number, y2: number) {
        return Math.sqrt(Math.pow(Math.abs(x1 - x2), 2) + Math.pow(Math.abs(y1 - y2), 2));
    }

    get points() {
        return this.data;
    }

    getAreas() {
        return this.areas;
    }

    load(storedData: string) {
        const json = JSON.parse(storedData);
        if (json.data) {
            this.data = json.data;
        }
    }

    onAreaClicked(area: Area, category: Category) {
        if (area.getSelectedValue() === category.value) {
            area.setSelectedValue(null); // unset radio
        } else {
            const areas = this.areas.filter(a => a.getSelectedValue() === category.value).sort((a, b) => a.selectionOrder - b.selectionOrder);
            if (areas.length >= category.max) {
                areas[0].setSelectedValue(null);
            }
            area.setSelectedValue(category.value);
        }
    }

    validate(): ValidateResult {
        switch (this.type) {
            case Highlighter.Type.PICK_AREA:
                return this.categories.map(c => {
                    const count = this.areas.filter(a => a.getSelectedValue() === c.value).length;
                    // Check only minimum - maximum cannot be exceeded (checked by ui)
                    if (count < c.min) {
                        return ValidateResult.errorResult(this.imageItem.questionary.messages.get('valuesCheckImageHighlighterCategoryHasTooFewItems',
                            {category: c.name, minimum: '' + c.min, selected: '' + count})
                        );
                    } else {
                        return ValidateResult.okResult();
                    }
                }).reduce((acc, c: ValidateResult) => acc.createMerged(c), ValidateResult.okResult());
            case Highlighter.Type.POINT:
                return this.categories.map(c => {
                    const count = this.points[c.value].length;
                    if (c.min !== 0 && count < c.min) {
                        return ValidateResult.errorResult(this.imageItem.questionary.messages.get('valuesCheckImageHighlighterCategoryHasTooFewItems',
                            {category: c.name, minimum: '' + c.min, selected: '' + count})
                        );
                    } else {
                        return ValidateResult.okResult();
                    }
                }).reduce((acc, c: ValidateResult) => acc.createMerged(c), ValidateResult.okResult());
            default:
                return ValidateResult.okResult();
        }
    }

    public save() {
        const value = {
            type: this.type,
            data: this.data
        };
        this.imageItem.set(JSON.stringify(value));
    }

    public reset() {
        ImageHighlighter.closeDialog();
        Object.keys(this.data).forEach(id => this.data[id] = []);
    }
}
export class ImageItem extends CellItem {
    viewport?: Viewport;
    scale?: Size;
    previewType: 'NONE'|'CLICK_TO_FULL_IMAGE'|string;
    file: string;
    highlighter: Highlighter;

    static fromXml(config: XmlTagInfo) {
        return new ImageItem(config);
    }

    constructor(config: XmlTagInfo) {
        super(config);

        this.viewport = Viewport.parseJson(config.attributes['viewport']);
        this.scale = Size.parseJson(config.attributes['scale']);

        this.previewType = config.attributes['previewType'];
        this.file = config.attributes['file'];
        this.highlighter = this.childrenTags
            .filter(node => node.tag === 'highlighter')
            .map(node => new Highlighter(this, node))
            [0];
    }


    init(questionary: Questionary, form: Form, cell: Cell): any {
        super.init(questionary, form, cell);
        if (this.hasHighlighter()) {
            this.highlighter.init(this, questionary);
        }
    }

    getView() {
        // @ts-ignore
        return m(ImageView.component, {cellItem: this});
    }

    hasViewport(): boolean {
        return !_.isUndefined(this.viewport);
    }

    hasScale(): boolean {
        return !_.isUndefined(this.scale);
    }

    hasHighlighter(): boolean {
        return !_.isUndefined(this.highlighter);
    }

    set(value: any) {
        this.setVariable(value);
        this.updateUnknowns();
        this.form.reValidate();
    }

    resourcesToPreload() {
        return [this.questionary.getFileUrl(this.file)];
    }

    validate(): ValidateResult {
        if (this.hasHighlighter()) {
            return this.highlighter.validate();
        } else {
            return ValidateResult.okResult();
        }
    }

    reset() {
        super.reset();
        this.highlighter?.reset();
    }
}

export module ImageItem {
    export class PreviewType {
        public static NONE = 'NONE';
        public static CLICK_TO_FULL_IMAGE = 'CLICK_TO_FULL_IMAGE';
    }
}

export module Highlighter {
    export class Type {
        public static NONE = 'NONE';
        public static POINT = 'POINT';
        public static PICK_AREA = 'PICK_AREA';
    }
}
